import { useState } from 'react';

import { Button, Group, Radio, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { useUpdatePatientMutation } from '@/store/slices/api/patientsApiSlice';

import {
  assistanceDecision,
  assistanceGate,
  AssistanceMode,
  assistanceOverride,
  decisionMessage,
  parseMoney,
  parsePeople,
  WaiverDecision
} from './financialAssistance';

type Step = 'gate' | 'apply' | 'decision' | 'message';
type GateChoice = 'new' | 'keep' | 'stop';
type ContinueChoice = 'continue' | 'stop';

const ALREADY_CHOICES = [
  { value: 'new' as const, label: 'Request a new amount' },
  { value: 'keep' as const, label: 'Leave as is' },
  { value: 'stop' as const, label: 'Patient does not want to continue' }
];

const CCAP_CHOICES = [
  { value: 'new' as const, label: 'Request financial assistance' },
  { value: 'keep' as const, label: 'Leave as is' }
];

const CONTINUE_CHOICES = [
  { value: 'continue' as const, label: 'Patient would like to continue' },
  { value: 'stop' as const, label: 'Patient does not want to continue' }
];

const OFFBOARD =
  'If the patient does not want to proceed, try changing the monitoring frequency within the allowed parameters. Otherwise, deactivate the patient.';

const FinancialAssistanceFlow = ({
  patientId,
  override,
  onBack,
  onClose
}: {
  patientId: string;
  override: string | null;
  onBack: () => void;
  onClose: () => void;
}) => {
  const gate = assistanceGate(override);
  const mode: AssistanceMode = gate.kind === 'ccap' ? 'ccap' : 'standard';
  const [step, setStep] = useState<Step>(gate.kind === 'apply' ? 'apply' : 'gate');
  const [gateChoice, setGateChoice] = useState<GateChoice | ''>('');
  const [continueChoice, setContinueChoice] = useState<ContinueChoice | ''>('');
  const [income, setIncome] = useState('');
  const [people, setPeople] = useState('');
  const [applyError, setApplyError] = useState('');
  const [decision, setDecision] = useState<WaiverDecision | null>(null);
  const [message, setMessage] = useState({ title: '', body: '' });
  const [updatePatient, { isLoading }] = useUpdatePatientMutation();

  const close = () => {
    if (!isLoading) onClose();
  };

  const back = () => {
    if (isLoading) return;
    if (step === 'decision') setStep('apply');
    else if (step === 'apply' && gate.kind !== 'apply') setStep('gate');
    else onBack();
  };

  const saveOverride = async (next: string | null) => {
    if (!next || next === override) return true;
    try {
      await updatePatient({
        patient: { id: patientId, financialPayerOverride: next }
      }).unwrap();
      return true;
    } catch {
      notifications.show({
        title: 'Financial assistance',
        message: 'The financial override could not be saved.',
        color: 'red'
      });
      return false;
    }
  };

  const finish = async (wantsToContinue: boolean) => {
    if (!decision) return;
    // Salesforce updates the patient before asking whether they want to continue,
    // so declining still keeps the override that was just applied.
    const next = assistanceOverride(decision, mode);
    const saved = await saveOverride(next);
    if (!saved) return;
    if (!wantsToContinue) {
      setMessage({ title: 'Patient does not want to continue', body: OFFBOARD });
    } else if (next) {
      setMessage({
        title: 'Financial assistance applied',
        body: `The financial override is now ${next}.`
      });
    } else {
      setMessage({
        title: 'Financial override unchanged',
        body: 'The application was denied, so the financial override was left as it is.'
      });
    }
    setStep('message');
  };

  const goFromGate = () => {
    if (gateChoice === 'new') {
      setStep('apply');
      return;
    }
    if (gateChoice === 'keep') {
      setMessage({
        title: gate.kind === 'ccap' ? 'CCAP unchanged' : 'Financial assistance unchanged',
        body:
          gate.kind === 'ccap'
            ? 'The patient will continue as CCAP, with cost share capped at $25 per encounter.'
            : 'The patient will continue with the current financial assistance.'
      });
      setStep('message');
      return;
    }
    if (gateChoice === 'stop') {
      setMessage({ title: 'Patient does not want to continue', body: OFFBOARD });
      setStep('message');
    }
  };

  const goFromApply = () => {
    const amount = parseMoney(income);
    const count = parsePeople(people);
    if (amount == null || count == null) {
      setApplyError('Enter the annual household income and the number of people in the household.');
      return;
    }
    setApplyError('');
    setDecision(assistanceDecision(amount, count));
    setContinueChoice('');
    setStep('decision');
  };

  const result = decision ? decisionMessage(decision, mode) : null;
  const nextOverride = decision ? assistanceOverride(decision, mode) : null;
  const notice =
    step === 'message' || (step === 'gate' && (gate.kind === 'self_pay' || gate.kind === 'qmb'));
  const primaryLabel = notice ? 'Close' : step === 'decision' ? 'Apply' : 'Next';
  const primaryDisabled =
    isLoading ||
    (step === 'gate' && (gate.kind === 'already' || gate.kind === 'ccap') && gateChoice === '') ||
    (step === 'decision' && continueChoice === '');

  return (
    <Stack gap="md">
      {step === 'gate' && gate.kind === 'self_pay' && (
        <Text size="sm">
          This patient is marked Self Pay. Add eligible insurance before applying for financial
          assistance.
        </Text>
      )}

      {step === 'gate' && gate.kind === 'qmb' && (
        <Text size="sm">
          This patient is a Qualified Medicare Beneficiary. They will not receive a statement from
          Rhythm for copays or deductibles. No financial assistance is necessary.
        </Text>
      )}

      {step === 'gate' && gate.kind === 'already' && (
        <>
          <Text size="sm">
            This patient is already on financial assistance. Current status: {gate.percent}% waiver.
          </Text>
          <Radio.Group
            label="Available actions"
            value={gateChoice}
            onChange={(value) => setGateChoice(value as GateChoice)}>
            <Stack gap="sm" mt="xs">
              {ALREADY_CHOICES.map((choice) => (
                <Radio key={choice.value} value={choice.value} label={choice.label} />
              ))}
            </Stack>
          </Radio.Group>
        </>
      )}

      {step === 'gate' && gate.kind === 'ccap' && (
        <>
          <Text size="sm">
            Copays and Rhythm cost are already capped at $25 per encounter. A further discount can
            be requested from household income.
          </Text>
          <Radio.Group
            label="Available actions"
            value={gateChoice}
            onChange={(value) => setGateChoice(value as GateChoice)}>
            <Stack gap="sm" mt="xs">
              {CCAP_CHOICES.map((choice) => (
                <Radio key={choice.value} value={choice.value} label={choice.label} />
              ))}
            </Stack>
          </Radio.Group>
        </>
      )}

      {step === 'apply' && (
        <>
          <Text size="sm" c="dimmed">
            Annual household income includes wages, jobs, alimony, and other amounts reported on
            yearly taxes. Household size includes every adult and child who lives there.
          </Text>
          <TextInput
            label="Household income"
            withAsterisk
            placeholder="0"
            inputMode="decimal"
            value={income}
            onChange={(event) => setIncome(event.currentTarget.value)}
          />
          <TextInput
            label="Number of people in household"
            withAsterisk
            placeholder="1"
            inputMode="numeric"
            value={people}
            onChange={(event) => setPeople(event.currentTarget.value)}
          />
          {applyError && (
            <Text size="sm" c="red">
              {applyError}
            </Text>
          )}
        </>
      )}

      {step === 'decision' && result && (
        <>
          <Text fw={600}>{result.title}</Text>
          <Text size="sm">{result.body}</Text>
          <Radio.Group
            label="Available actions"
            value={continueChoice}
            onChange={(value) => setContinueChoice(value as ContinueChoice)}>
            <Stack gap="sm" mt="xs">
              {CONTINUE_CHOICES.map((choice) => (
                <Radio key={choice.value} value={choice.value} label={choice.label} />
              ))}
            </Stack>
          </Radio.Group>
          <Text size="sm" c="dimmed">
            {nextOverride
              ? `Applying this decision sets the financial override to ${nextOverride}.`
              : 'A denial does not change the financial override.'}
          </Text>
        </>
      )}

      {step === 'message' && (
        <>
          <Text fw={600}>{message.title}</Text>
          <Text size="sm">{message.body}</Text>
        </>
      )}

      <Group justify="flex-end">
        {!notice && (
          <Button variant="default" disabled={isLoading} onClick={back}>
            Back
          </Button>
        )}
        {notice && (
          <Button variant="default" onClick={onBack}>
            Back
          </Button>
        )}
        <Button
          loading={isLoading}
          disabled={primaryDisabled}
          onClick={() => {
            if (notice) close();
            else if (step === 'gate') goFromGate();
            else if (step === 'apply') goFromApply();
            else if (continueChoice) void finish(continueChoice === 'continue');
          }}>
          {primaryLabel}
        </Button>
      </Group>
    </Stack>
  );
};

export default FinancialAssistanceFlow;
